/**
 * The RFI register and the RFI screen (wave W2, Mahmoud's R10).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * His specification: "RFIs get their OWN register: the necessary columns, groupable by
 * contract, action links per row, and their own screen with a history of everything that
 * happened in the RFI so far (timestamped)." The register is grouped by contract and
 * says on every row whose turn it is; the screen carries the request, the date
 * agreement's answer for THIS caller (only the moves the server offers), and the merged,
 * timestamped history.
 *
 * Phone first (coherence review N02, bound into W2: the RFI loop is the inspector's phone
 * loop): the register is a list of cards below 720px, never a table that runs off the
 * edge, and every act is a full-width touch target.
 */

import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router";

import { uploadAttachment } from "../api/attachments";
import type { Classification } from "../api/credentials";
import { ClassificationLine } from "../components/Classification";
import { HistoryButton } from "../components/HistoryDrawer";
import { ApiError } from "../api/client";
import { QueueBanner } from "../components/QueueBanner";
import {
  INSPECTION_DONE,
  ProjectFilterBanner,
  useProjectContracts,
  useProjectFilter,
} from "./projectFilter";
import type { Me } from "../api/me";
import {
  cancelRfi,
  delegateRfi,
  getRfi,
  listEligibleAssignees,
  listRfis,
  assignRfi,
  recordRfiDateMove,
  rejectRfi,
  releaseWitness,
  type PersonOption,
  type RfiDetail,
  type RfiMove,
  type RfiSummary,
  waiveRfi,
} from "../api/rfis";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import { FilePicker } from "@/components/ui/file-picker";
import { ServerField } from "../lib/serverErrors";
import { zonedStamp } from "../lib/zonedTime";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { ConductDialog } from "./Conduct";
import { DownloadLink } from "./Inspections";
import {
  AbsencePanel,
  Breadcrumbs,
  EmptyState,
  Facts,
  Section,
  SkeletonFacts,
  ShortNoticeChip,
  SkeletonRows,
  StateChip,
  day,
  stamp,
} from "./parts";
import { useQuery } from "./useQuery";

/**
 * Wave RFI ruling 5: the request's files by `attachment_ids`, in link order, each with
 * its listed name when the attachments carry it (they always do today; an id the list
 * does not name still offers its download).
 */
function rfiFiles(rfi: RfiDetail): {
  attachmentId: string;
  filename: string | null;
  fileSizeBytes: number | null;
  linkedBy: string | null;
  classification: Classification | null;
}[] {
  const ids =
    rfi.attachmentIds.length > 0
      ? rfi.attachmentIds
      : rfi.attachments.map((one) => one.attachmentId);
  return [...new Set(ids)].map((id) => {
    const listed = rfi.attachments.find((one) => one.attachmentId === id);
    return {
      attachmentId: id,
      filename: listed?.filename ?? null,
      fileSizeBytes: listed?.fileSizeBytes ?? null,
      linkedBy: listed?.linkedBy ?? null,
      classification: listed?.classification ?? null,
    };
  });
}

/** Whether the row waits on THIS side (the register's "Your turn" marker). */
function yourTurn(row: RfiSummary, operator: boolean): boolean {
  return operator ? row.dateState === "AWAITING_OPERATOR" : row.dateState === "AWAITING_CONTRACTOR";
}

export function RfisPage(): React.JSX.Element {
  const token = useToken();
  const { me } = useOutletContext<{ me: Me }>();
  const [searchParams] = useSearchParams();
  const isQueue = searchParams.get("queue") === "true";
  // Wave b12 frontend item 12: a Home card's "Open RFIs" opens the register narrowed to
  // the project's contracts and the requests not yet over, both origins counted (the
  // card counts the contractor's and the operator's requests alike).
  const project = useProjectFilter();
  const projectContracts = useProjectContracts(project?.id ?? null);
  const openOnly = searchParams.get("open") === "1";
  const operator = me.workflowClass !== "contractor" && me.workflowClass !== "tpi";
  // The contractor's requests. The operator's own requests share the date loop and the
  // calendars, and are listed under the program they were sent from. In queue mode,
  // the pre-filtered open RFI queue for this persona is read.
  const register = useQuery(
    (signal) =>
      listRfis(
        token,
        isQueue
          ? { queue: true, signal }
          : project !== null
            ? { signal }
            : { origin: "RFI", signal },
      ),
    [token, isQueue, project?.id],
  );
  const rows = (register.kind === "ready" ? register.data.items : []).filter(
    (row) =>
      (projectContracts === null || projectContracts.has(row.contractId)) &&
      (!openOnly || !INSPECTION_DONE.includes(row.workflowState)),
  );

  const groups = new Map<string, RfiSummary[]>();
  for (const row of rows) {
    const key = `${row.contractHumanId} · ${row.contractorPartyName}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  return (
    <>
      <h2 className="q-shell__heading">{strings.rfis.heading}</h2>
      <p className="q-shell__lede">{operator ? strings.rfis.lede : strings.rfis.contractorLede}</p>
      {isQueue && (
        <QueueBanner
          label={strings.queues.rfiQueue.label}
          count={register.kind === "ready" ? register.data.items.length : null}
          wholeRegister="/rfis"
        />
      )}
      {register.kind === "loading" && <SkeletonRows rows={6} columns={5} />}
      {register.kind === "absence" && <AbsencePanel />}
      {register.kind === "failed" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {project !== null && (
        <ProjectFilterBanner
          label={project.label}
          view={openOnly ? strings.home.cardRfisOpen : strings.rfis.heading}
          register="/rfis"
        />
      )}
      {register.kind === "ready" && rows.length === 0 && (
        <EmptyState
          title={isQueue ? strings.queues.rfiQueue.empty : strings.rfis.emptyTitle}
          body={isQueue ? strings.queues.rfiQueue.emptyBody : strings.rfis.emptyBody}
        />
      )}
      {[...groups].map(([contract, rows]) => (
        <section key={contract} className="q-rfi-group" data-testid="rfi-group">
          <h3 className="q-rfi-group__heading">
            {strings.rfis.contractGroup(contract, rows.length)}
          </h3>
          <ul className="q-rfi-list">
            {rows.map((row) => (
              <li key={row.id} className="q-rfi-card" data-testid="rfi-row">
                <div className="q-rfi-card__top">
                  <Link to={`/rfis/${row.id}`} className="q-register__mono">
                    {row.humanId}
                  </Link>
                  {yourTurn(row, operator) && (
                    <span className="q-chip q-chip--attention" data-testid="rfi-your-turn">
                      {strings.rfis.yourTurn}
                    </span>
                  )}
                  <ShortNoticeChip rfi={row} />
                  {row.itemCount > 1 && (
                    <span className="q-chip" data-testid="rfi-row-items">
                      {strings.rfis.itemsCount(row.itemCount)}
                    </span>
                  )}
                  {row.attachmentCount > 0 && (
                    <span className="q-chip" data-testid="rfi-row-files">
                      {strings.rfis.filesCount(row.attachmentCount)}
                    </span>
                  )}
                </div>
                <p className="q-rfi-card__scope">{row.scope}</p>
                <p className="q-rfi-card__meta">
                  {strings.rfis.columnItem}: {row.itpHumanId} {row.itemNo ?? ""} ·{" "}
                  {term(row.inspectionType)}
                  {row.instanceLabel !== null && (
                    <span data-testid="rfi-row-instance">
                      {" "}
                      · {strings.rfis.factsInstance}: {row.instanceLabel}
                    </span>
                  )}
                </p>
                <p className="q-rfi-card__meta">
                  <strong>{day(row.plannedDate)}</strong> ·{" "}
                  {strings.rfis.dateState[row.dateState] ?? row.dateState}
                  {row.pendingDate !== null &&
                    ` · ${strings.rfis.pendingLede(day(row.pendingDate))}`}
                </p>
                <Link
                  to={`/rfis/${row.id}`}
                  className="q-button q-button--quiet q-rfi-card__open"
                  data-testid="rfi-open"
                >
                  {strings.rfis.open}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

export function formatChainRankOrRole(displayName: string, index: number): string {
  const lower = displayName.toLowerCase();
  let role: string | null = null;
  if (lower.includes("inspector")) {
    role = "Discipline inspector";
  } else if (lower.includes("engineer")) {
    role = "QA/QC engineer";
  } else if (lower.includes("lead") || lower.includes("qc")) {
    role = "QC lead";
  } else if (lower.includes("manager") || lower.includes("power")) {
    role = "Manager";
  }
  const rank = `Chain rank ${String(index + 1)}`;
  return role ? `(${role} · ${rank})` : `(${rank})`;
}

const MOVE_WORDS: Record<RfiMove, (detail: RfiDetail) => string> = {
  CONFIRM: () => strings.rfis.accept,
  PROPOSE: () => strings.rfis.counter,
  ACKNOWLEDGE: (detail) => strings.rfis.agree(day(detail.pendingDate ?? detail.plannedDate)),
};

export function RfiDetailPage(): React.JSX.Element {
  const token = useToken();
  const outlet = useOutletContext<{ me?: Me | null } | undefined>();
  const me = outlet?.me ?? null;
  const { rfiId = "" } = useParams();
  const [reload, setReload] = useState(0);
  const [dialog, setDialog] = useState<RfiMove | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [waiving, setWaiving] = useState(false);
  // Wave INST ruling 4: an AUTHORITY_REVIEW task opens the second hand's dialog here.
  const [searchParams] = useSearchParams();
  const [authorityDismissed, setAuthorityDismissed] = useState(false);
  const [conducting, setConducting] = useState(false);
  const [delegating, setDelegating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const detail = useQuery(() => getRfi(token, rfiId), [token, rfiId, reload]);

  if (detail.kind === "absence") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.rfis.detailHeading}</h2>
        <AbsencePanel />
      </>
    );
  }
  if (detail.kind !== "ready") {
    return (
      <>
        <h2 className="q-shell__heading">{strings.rfis.detailHeading}</h2>
        {detail.kind === "loading" && <SkeletonFacts fields={6} />}
        {detail.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
      </>
    );
  }
  const rfi = detail.data;
  const byOperator = rfi.origin === "OPERATOR_REQUEST";
  // Wave INST ruling 4: a proposed waiver waits on the engineering authority; the server's
  // may_waive answers per hand, so a pending proposal the caller may act on is completed.
  const waiverPending = rfi.waiverState === "PENDING_AUTHORITY";
  const completesWaiver = waiverPending && rfi.mayWaive && !rfi.waiverProposedByMe;
  const authorityOpen =
    completesWaiver && searchParams.get("act") === "authority" && !authorityDismissed;
  const mayConduct = rfi.conductActs.includes("START") || rfi.conductActs.includes("SAVE");
  const isAssignee = Boolean(me?.id && rfi.assigneePersonId && me.id === rfi.assigneePersonId);
  const mayDelegate =
    isAssignee &&
    (rfi.workflowState === "DISTRIBUTED" ||
      rfi.workflowState === "IN_ACTION" ||
      rfi.dateState === "AGREED");

  return (
    <>
      <Breadcrumbs trail={[{ label: strings.rfis.heading, to: "/rfis" }, { label: rfi.humanId }]} />
      <h2 className="q-shell__heading" data-testid="rfi-title">
        {rfi.humanId} {rfi.scope}
      </h2>
      {byOperator && (
        <p className="q-note" data-testid="rfi-origin">
          {strings.rfis.fromOperator(rfi.verificationTypeLabel)}
        </p>
      )}
      <p className="q-shell__lede" data-testid="rfi-state">
        <StateChip state={rfi.workflowState} /> <ShortNoticeChip rfi={rfi} />{" "}
        {strings.rfis.dateState[rfi.dateState] ?? rfi.dateState} ·{" "}
        {rfi.dateState === "AGREED"
          ? strings.rfis.agreedLede(day(rfi.plannedDate))
          : rfi.pendingDate !== null
            ? strings.rfis.pendingLede(day(rfi.pendingDate))
            : day(rfi.plannedDate)}
      </p>
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}

      <div className="q-section-bands" data-testid="rfi-detail-sections">
        <Section heading={strings.rfis.actionsHeading}>
          <div className="q-actions-row q-rfi-acts" data-testid="rfi-actions">
            <HistoryButton objectType="VERIFICATION" objectId={rfi.id} recordLabel={rfi.humanId} />
            {rfi.availableMoves.map((move) => (
              <button
                key={move}
                type="button"
                className={
                  move === "PROPOSE" ? "q-button q-button--quiet" : "q-button q-mini--primary"
                }
                data-testid={`rfi-${move.toLowerCase()}`}
                onClick={() => {
                  setDialog(move);
                }}
              >
                {MOVE_WORDS[move](rfi)}
              </button>
            ))}
            {rfi.mayCancel && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="rfi-cancel"
                onClick={() => {
                  setCancelling(true);
                }}
              >
                {strings.rfis.cancel}
              </button>
            )}
            {rfi.mayWaive && !waiverPending && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="rfi-waive"
                onClick={() => {
                  setWaiving(true);
                }}
              >
                {strings.rfis.waive}
              </button>
            )}
            {completesWaiver && (
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="rfi-authority-review"
                onClick={() => {
                  setWaiving(true);
                }}
              >
                {strings.rfis.authorityOpen}
              </button>
            )}
            {rfi.mayReject && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="rfi-reject"
                onClick={() => {
                  setRejecting(true);
                }}
              >
                {strings.rfis.reject}
              </button>
            )}
            {mayConduct && (
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="rfi-conduct"
                onClick={() => {
                  setConducting(true);
                }}
              >
                {rfi.conductActs.includes("SAVE")
                  ? strings.conduct.continueDraft
                  : strings.conduct.begin}
              </button>
            )}
            {rfi.mayAssign && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="rfi-assign"
                onClick={() => {
                  setAssigning(true);
                }}
              >
                {rfi.assigneePersonId === null ? strings.rfis.assign : strings.rfis.reassign}
              </button>
            )}
            {rfi.mayReleaseWitness && (
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="rfi-release-witness"
                onClick={() => {
                  setReleasing(true);
                }}
              >
                {strings.rfis.releaseWitness}
              </button>
            )}
            {mayDelegate && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="rfi-delegate"
                onClick={() => {
                  setDelegating(true);
                }}
              >
                {strings.rfis.delegate}
              </button>
            )}
            {rfi.availableMoves.length === 0 &&
              !rfi.mayReject &&
              !rfi.mayCancel &&
              !rfi.mayWaive &&
              !completesWaiver &&
              !mayConduct &&
              !rfi.mayReleaseWitness &&
              !rfi.mayAssign &&
              !mayDelegate && (
                <p className="q-note" data-testid="rfi-no-moves">
                  {strings.rfis.noMoves}
                </p>
              )}
          </div>
        </Section>

        {waiverPending && (
          <Section heading={strings.rfis.waiverPendingHeading}>
            <div className="q-subform" data-testid="rfi-waiver-pending">
              <p>
                <StateChip state={rfi.waiverState} />{" "}
                {rfi.waiverProposedByMe
                  ? strings.rfis.waiverPendingMine
                  : strings.rfis.waiverPendingOther}
              </p>
              <p className="q-field__label">{strings.rfis.authorityProposedReason}</p>
              <p className="q-thread__body">{rfi.waiverReason ?? ""}</p>
              {rfi.waiverProposedAt !== null && (
                <p className="q-note">
                  {strings.rfis.waiverProposedOn(stamp(rfi.waiverProposedAt))}
                </p>
              )}
            </div>
          </Section>
        )}

        {(rfi.operatorUnattendedAt !== null || rfi.witnessReleased) && (
          <Section heading={strings.rfis.witnessHeading}>
            <div className="q-subform" data-testid="rfi-witness">
              {rfi.witnessReleased && (
                <p>
                  <span className="q-chip q-chip--attention" data-testid="rfi-witness-released">
                    {strings.rfis.witnessReleasedChip}
                  </span>{" "}
                  {rfi.witnessReleasedAt !== null && (
                    <span data-testid="rfi-witness-released-at">
                      {strings.rfis.witnessReleasedOn(zonedStamp(rfi.witnessReleasedAt))}
                    </span>
                  )}
                </p>
              )}
              {rfi.operatorUnattendedAt !== null && (
                <p className="q-note" data-testid="rfi-witness-unattended">
                  {strings.rfis.witnessUnattended(zonedStamp(rfi.operatorUnattendedAt))}
                </p>
              )}
              {rfi.witnessReleaseDeclaration !== null && (
                <>
                  <p className="q-field__label">{strings.rfis.witnessDeclarationLabel}</p>
                  <p className="q-thread__body" data-testid="rfi-witness-declaration">
                    {rfi.witnessReleaseDeclaration}
                  </p>
                </>
              )}
            </div>
          </Section>
        )}

        <Section heading={strings.rfis.detailHeading}>
          <Facts
            entries={[
              [strings.rfis.factsScope, <span key="scope">{rfi.scope}</span>],
              ...(rfi.itpId !== null
                ? [
                    [
                      strings.rfis.factsItem,
                      <span key="item">
                        <Link className="q-link" to={`/itps/${rfi.itpId}`}>
                          {rfi.itpHumanId}
                        </Link>{" "}
                        {rfi.itemNo ?? ""}
                      </span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
              ...(rfi.itpItemId !== null || rfi.instanceLabel !== null
                ? [
                    [
                      strings.rfis.factsInstance,
                      <span key="instance" data-testid="rfi-instance">
                        {rfi.instanceLabel ?? strings.rfis.factsWholeItem}
                      </span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
              [
                strings.rfis.factsQuantity,
                <span key="quantity" data-testid="rfi-quantity">
                  {rfi.quantity}
                </span>,
              ],
              ...(rfi.externalAssetId !== null
                ? [
                    [
                      strings.rfis.factsAsset,
                      <span key="asset" data-testid="rfi-asset">
                        {rfi.externalAssetId}
                      </span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
              ...(rfi.internalCheckReference !== null
                ? [
                    [
                      strings.rfis.factsCheckReference,
                      <span key="check" data-testid="rfi-check-reference">
                        {rfi.internalCheckReference}
                      </span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
              ...(rfi.shortNotice
                ? [
                    [
                      strings.rfis.factsShortNotice,
                      <span key="short" data-testid="rfi-short-notice-justification">
                        {rfi.shortNoticeJustification ?? ""}
                      </span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
              [strings.rfis.factsProgram, <span key="program">{rfi.verificationTypeLabel}</span>],
              ...(rfi.disciplineLabel !== null
                ? [
                    [
                      strings.rfis.factsDiscipline,
                      <span key="discipline">{rfi.disciplineLabel}</span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
              [strings.rfis.factsType, <span key="type">{term(rfi.inspectionType)}</span>],
              [strings.rfis.factsContract, <span key="contract">{rfi.contractHumanId}</span>],
              [strings.rfis.factsContractor, <span key="party">{rfi.contractorPartyName}</span>],
              [strings.rfis.factsLocation, <span key="location">{rfi.location ?? ""}</span>],
              [strings.rfis.factsDate, <span key="date">{day(rfi.plannedDate)}</span>],
              ...(rfi.assigneeDisplayName !== null
                ? [
                    [
                      strings.rfis.factsAssignee,
                      <span key="assignee" data-testid="rfi-assignee">
                        {rfi.assigneeDisplayName}
                      </span>,
                    ] as [string, React.ReactNode],
                  ]
                : []),
            ]}
          />
          {rfi.dateState === "AGREED" && (
            <p className="q-gap-top">
              <Link
                className="q-link"
                to={`/inspections/${rfi.id}`}
                data-testid="rfi-open-inspection"
              >
                {strings.rfis.openInspection}
              </Link>
            </p>
          )}
        </Section>

        {rfi.items.length > 1 && (
          <Section heading={strings.rfis.itemsHeading}>
            <ol className="q-history" data-testid="rfi-items">
              {[...rfi.items]
                .sort((a, b) => a.position - b.position)
                .map((one) => (
                  <li key={one.itpItemId} className="q-history__row" data-testid="rfi-item">
                    <strong>
                      {strings.rfis.itemsColumnItem} {one.itemNo}
                    </strong>{" "}
                    · {strings.rfis.itemsColumnInstance}:{" "}
                    {one.instanceLabel ?? strings.rfis.factsWholeItem} ·{" "}
                    {strings.rfis.itemsColumnQuantity}: {one.quantity}
                  </li>
                ))}
            </ol>
          </Section>
        )}

        <Section heading={strings.rfis.attachmentsHeading}>
          {rfiFiles(rfi).length === 0 ? (
            <p className="q-attachment">{strings.rfis.attachmentsEmpty}</p>
          ) : (
            rfiFiles(rfi).map((entry, index) => (
              <p className="q-attachment" key={entry.attachmentId} data-testid="rfi-attachment">
                <DownloadLink
                  attachmentId={entry.attachmentId}
                  filename={entry.filename ?? strings.rfis.fileUnnamed(index + 1)}
                />
                {entry.fileSizeBytes !== null &&
                  ` (${String(Math.max(1, Math.round(entry.fileSizeBytes / 1024)))} kB)`}{" "}
                <ClassificationLine
                  attachmentId={entry.attachmentId}
                  filename={entry.filename ?? strings.rfis.fileUnnamed(index + 1)}
                  classification={entry.classification}
                  mayClassify={
                    (me !== null &&
                      me.workflowClass !== "contractor" &&
                      me.workflowClass !== "tpi") ||
                    (me !== null && entry.linkedBy === me.id)
                  }
                  onClassified={(message) => {
                    setToast(message);
                    setReload((n) => n + 1);
                  }}
                />
              </p>
            ))
          )}
        </Section>
        <Section heading={strings.rfis.historyHeading}>
          <ol className="q-thread" data-testid="rfi-history">
            {rfi.history.map((entry) => (
              <li className="q-thread__entry" key={entry.id}>
                <p className="q-thread__meta">
                  <strong>
                    {entry.kind === "DATE"
                      ? (strings.rfis.historyDateMove[entry.event] ?? term(entry.event))
                      : (strings.rfis.historyTransition[entry.event] ?? term(entry.event))}
                  </strong>
                  {entry.proposedDate !== null && ` · ${day(entry.proposedDate)}`}
                  {entry.actorDisplayName !== null && ` · ${entry.actorDisplayName}`}
                  {entry.actingPartyName !== null && ` · ${entry.actingPartyName}`} ·{" "}
                  {stamp(entry.at)}
                </p>
                {entry.text !== null && <p className="q-thread__body">{entry.text}</p>}
              </li>
            ))}
          </ol>
        </Section>
      </div>

      {cancelling && (
        <RfiCancelDialog
          rfi={rfi}
          onClose={() => {
            setCancelling(false);
          }}
          onDone={() => {
            setCancelling(false);
            setToast(strings.rfis.cancelled);
            setReload((n) => n + 1);
          }}
        />
      )}
      {(waiving || authorityOpen) && (
        <RfiWaiveDialog
          rfi={rfi}
          completing={completesWaiver}
          onClose={() => {
            setWaiving(false);
            setAuthorityDismissed(true);
          }}
          onDone={() => {
            setWaiving(false);
            setAuthorityDismissed(true);
            setToast(completesWaiver ? strings.rfis.waived : strings.rfis.waiverProposed);
            setReload((n) => n + 1);
          }}
        />
      )}
      {rejecting && (
        <RfiRejectDialog
          rfi={rfi}
          onClose={() => {
            setRejecting(false);
          }}
          onDone={() => {
            setRejecting(false);
            setToast(strings.rfis.rejected);
            setReload((n) => n + 1);
          }}
        />
      )}
      {conducting && (
        <ConductDialog
          verificationId={rfi.id}
          canDelegate={mayDelegate}
          onDelegate={() => {
            setConducting(false);
            setDelegating(true);
          }}
          onClose={() => {
            setConducting(false);
            setReload((n) => n + 1);
          }}
          onChanged={(message) => {
            if (message !== "") {
              setConducting(false);
              setToast(message);
            }
            setReload((n) => n + 1);
          }}
        />
      )}
      {assigning && (
        <RfiAssignDialog
          rfi={rfi}
          onClose={() => {
            setAssigning(false);
          }}
          onDone={(name) => {
            setAssigning(false);
            setToast(strings.rfis.assigned(name));
            setReload((n) => n + 1);
          }}
        />
      )}
      {releasing && (
        <RfiReleaseWitnessDialog
          rfi={rfi}
          onClose={() => {
            setReleasing(false);
          }}
          onDone={() => {
            setReleasing(false);
            setToast(strings.rfis.released);
            setReload((n) => n + 1);
          }}
        />
      )}
      {delegating && (
        <RfiDelegateDialog
          rfi={rfi}
          onClose={() => {
            setDelegating(false);
          }}
          onDone={(message) => {
            setDelegating(false);
            setToast(message);
            setReload((n) => n + 1);
          }}
        />
      )}
      {dialog !== null && (
        <RfiMoveDialog
          rfi={rfi}
          move={dialog}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(message) => {
            setDialog(null);
            setToast(message);
            setReload((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

function RfiMoveDialog({
  rfi,
  move,
  onClose,
  onDone,
}: {
  readonly rfi: RfiDetail;
  readonly move: RfiMove;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [inspectors, setInspectors] = useState<readonly string[]>([]);
  const [assigneeId, setAssigneeId] = useState(rfi.assigneePersonId ?? "");
  const [assigneeOptions, setAssigneeOptions] = useState<readonly PersonOption[]>(
    rfi.assigneeOptions.length > 0 ? rfi.assigneeOptions : rfi.inspectorOptions,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // The operator's side is the one offered CONFIRM; the contractor's, ACKNOWLEDGE.
  const operator = rfi.availableMoves.includes("CONFIRM");
  const needsDate = move === "PROPOSE";
  const needsReason = move === "PROPOSE" && operator;
  const ready = (!needsDate || date.length > 0) && (!needsReason || reason.trim().length > 0);
  const title =
    move === "CONFIRM"
      ? strings.rfis.acceptHeading
      : move === "ACKNOWLEDGE"
        ? strings.rfis.agreeHeading
        : strings.rfis.counterHeading;

  useEffect(() => {
    if (!operator) {
      return;
    }
    let active = true;
    void listEligibleAssignees(token, rfi.id)
      .then((opts) => {
        if (active && opts.length > 0) {
          setAssigneeOptions(opts);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token, rfi.id, operator]);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await recordRfiDateMove(token, rfi.id, {
        move,
        proposed_date: needsDate ? date : null,
        justification: reason.trim().length > 0 ? reason.trim() : null,
        inspector_person_ids: move === "CONFIRM" ? [...inspectors] : [],
        assignee_person_id: assigneeId.trim() === "" ? null : assigneeId.trim(),
      });
      onDone(strings.rfis.moveDone[move] ?? strings.schedules.actionToastDone(title));
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={`${rfi.humanId}: ${title}`} onClose={onClose} error={error}>
      {move === "CONFIRM" && (
        <>
          <p className="q-shell__lede">
            {strings.rfis.acceptLede(day(rfi.pendingDate ?? rfi.plannedDate))}
          </p>
          <fieldset className="q-field">
            <legend className="q-field__label">{strings.rfis.inspectorsLabel}</legend>
            {rfi.inspectorOptions.map((person) => (
              <label key={person.personId} className="q-check">
                <input
                  type="checkbox"
                  checked={inspectors.includes(person.personId)}
                  onChange={(event) => {
                    setInspectors((current) =>
                      event.target.checked
                        ? [...current, person.personId]
                        : current.filter((one) => one !== person.personId),
                    );
                  }}
                />{" "}
                {person.displayName}
              </label>
            ))}
            <p className="q-note">{strings.rfis.inspectorsNone}</p>
          </fieldset>
        </>
      )}
      {move === "ACKNOWLEDGE" && (
        <p className="q-shell__lede">
          {strings.rfis.agreeLede(day(rfi.pendingDate ?? rfi.plannedDate))}
        </p>
      )}
      {operator && (move === "CONFIRM" || move === "PROPOSE") && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="rfi-move-assignee">
            {strings.rfis.assigneeLabel}
          </label>
          <ServerField field="assignee_person_id">
            <select
              id="rfi-move-assignee"
              className="q-input"
              data-testid="rfi-move-assignee"
              value={assigneeId}
              onChange={(event) => {
                setAssigneeId(event.target.value);
              }}
            >
              <option value="">{strings.rfis.assigneeChoose}</option>
              {assigneeOptions.map((person, index) => (
                <option key={person.personId} value={person.personId}>
                  {person.displayName} {formatChainRankOrRole(person.displayName, index)}
                </option>
              ))}
            </select>
          </ServerField>
        </div>
      )}
      {needsDate && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="rfi-move-date">
            {strings.rfis.dateLabel}
          </label>
          <ServerField field="proposed_date">
            <input
              id="rfi-move-date"
              className="q-input"
              type="date"
              data-testid="rfi-move-date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      {move === "PROPOSE" && (
        <div className="q-field">
          <label className="q-field__label" htmlFor="rfi-move-reason">
            {operator ? strings.rfis.counterReasonLabel : strings.rfis.reproposeReasonLabel}
          </label>
          <ServerField field="justification">
            <textarea
              id="rfi-move-reason"
              className="q-textarea"
              data-testid="rfi-move-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </ServerField>
        </div>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-move-send"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {move === "PROPOSE" ? strings.rfis.send : title}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/** The operator refuses the contractor's request, saying why (the contractor is told). */
function RfiRejectDialog({
  rfi,
  onClose,
  onDone,
}: {
  readonly rfi: RfiDetail;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await rejectRfi(token, rfi.id, reason.trim());
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${rfi.humanId}: ${strings.rfis.rejectHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.rfis.rejectLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-reject-reason">
          {strings.rfis.rejectReasonLabel}
        </label>
        <ServerField field="reason">
          <textarea
            id="rfi-reject-reason"
            className="q-textarea"
            data-testid="rfi-reject-reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-reject-send"
          disabled={busy || reason.trim() === ""}
          onClick={() => {
            void send();
          }}
        >
          {strings.rfis.reject}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

function RfiCancelDialog({
  rfi,
  onClose,
  onDone,
}: {
  rfi: RfiDetail;
  onClose: () => void;
  onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await cancelRfi(token, rfi.id, reason.trim() || undefined);
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${rfi.humanId}: ${strings.rfis.cancelHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.rfis.cancelLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-cancel-reason">
          {strings.rfis.cancelReasonLabel}
        </label>
        <ServerField field="reason">
          <textarea
            id="rfi-cancel-reason"
            className="q-textarea"
            data-testid="rfi-cancel-reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-cancel-send"
          disabled={busy}
          onClick={() => {
            void send();
          }}
        >
          {strings.rfis.cancel}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/**
 * The waiver in two hands (wave INST rulings 4 and 9). The operator's call PROPOSES it
 * with its reason; a different person holding the engineering authority on the contract
 * completes it on the same route, the proposer's reason before them. The reason is
 * required on both hands (422 rfi.waiver_reason_required, said inside the dialog).
 */
function RfiWaiveDialog({
  rfi,
  completing,
  onClose,
  onDone,
}: {
  rfi: RfiDetail;
  /** The authority's second hand: the proposal is shown and completed. */
  completing: boolean;
  onClose: () => void;
  onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const said = reason.trim() !== "";

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await waiveRfi(token, rfi.id, reason);
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${rfi.humanId}: ${completing ? strings.rfis.authorityHeading : strings.rfis.waiveHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">
        {completing ? strings.rfis.authorityLede : strings.rfis.waiveLede}
      </p>
      {completing && (
        <div className="q-subform" data-testid="rfi-authority-proposal">
          <p className="q-field__label">{strings.rfis.authorityProposedReason}</p>
          <p className="q-thread__body" data-testid="rfi-authority-reason">
            {rfi.waiverReason ?? ""}
          </p>
          {rfi.waiverProposedAt !== null && (
            <p className="q-note">{strings.rfis.waiverProposedOn(stamp(rfi.waiverProposedAt))}</p>
          )}
        </div>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-waive-reason">
          {completing ? strings.rfis.authorityReasonLabel : strings.rfis.waiveReasonLabel}
        </label>
        <ServerField field="reason">
          <textarea
            id="rfi-waive-reason"
            className="q-textarea"
            data-testid="rfi-waive-reason"
            aria-required="true"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {!said && (
        <p className="q-note" data-testid="rfi-waive-reason-needed">
          {strings.rfis.waiveReasonNeeded}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-waive-send"
          disabled={busy || !said}
          onClick={() => {
            void send();
          }}
        >
          {completing ? strings.rfis.authorityComplete : strings.rfis.waivePropose}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

export function RfiDelegateDialog({
  rfi,
  onClose,
  onDone,
}: {
  readonly rfi: RfiDetail;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [assigneeId, setAssigneeId] = useState("");
  const [reason, setReason] = useState("");
  const [options, setOptions] = useState<readonly PersonOption[]>(
    (rfi.assigneeOptions.length > 0 ? rfi.assigneeOptions : rfi.inspectorOptions).filter(
      (p) => p.personId !== rfi.assigneePersonId,
    ),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let active = true;
    void listEligibleAssignees(token, rfi.id)
      .then((opts) => {
        if (active && opts.length > 0) {
          setOptions(opts.filter((p) => p.personId !== rfi.assigneePersonId));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token, rfi.id, rfi.assigneePersonId]);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await delegateRfi(token, rfi.id, {
        assignee_person_id: assigneeId,
        reason: reason.trim().length > 0 ? reason.trim() : null,
      });
      onDone(strings.rfis.delegatedDone);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${rfi.humanId}: ${strings.rfis.delegateHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.rfis.delegateLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-delegate-assignee">
          {strings.rfis.delegateAssigneeLabel}
        </label>
        <ServerField field="assignee_person_id">
          <select
            id="rfi-delegate-assignee"
            className="q-input"
            data-testid="rfi-delegate-assignee"
            value={assigneeId}
            onChange={(event) => {
              setAssigneeId(event.target.value);
            }}
          >
            <option value="">{strings.rfis.delegateChooseAssignee}</option>
            {options.map((person, index) => (
              <option key={person.personId} value={person.personId}>
                {person.displayName} {formatChainRankOrRole(person.displayName, index)}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-delegate-reason">
          {strings.rfis.delegateReasonLabel}
        </label>
        <ServerField field="reason">
          <textarea
            id="rfi-delegate-reason"
            className="q-textarea"
            data-testid="rfi-delegate-reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-delegate-send"
          disabled={busy || assigneeId.trim() === ""}
          onClick={() => {
            void send();
          }}
        >
          {strings.rfis.delegateSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/**
 * Wave COM item 3: the contractor releases the witness point the operator missed. The
 * declaration is required (said as needed until written; the server's 422
 * rfi.release_declaration_required inside the dialog); the evidence is uploaded once per
 * file and sent by id. Every refusal is the server's sentence, inside the dialog:
 * rfi.witness_not_released names the operator's deadline, and a hold point is never
 * released (rfi.hold_point_never_released).
 */
function RfiReleaseWitnessDialog({
  rfi,
  onClose,
  onDone,
}: {
  readonly rfi: RfiDetail;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [declaration, setDeclaration] = useState("");
  const [evidence, setEvidence] = useState<readonly File[]>([]);
  const [uploaded] = useState(() => new Map<File, string>());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const said = declaration.trim() !== "";

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const ids: string[] = [];
      for (const file of evidence) {
        let id = uploaded.get(file);
        if (id === undefined) {
          id = await uploadAttachment(token, file);
          uploaded.set(file, id);
        }
        ids.push(id);
      }
      await releaseWitness(token, rfi.id, {
        declaration: said ? declaration.trim() : null,
        attachment_ids: ids,
      });
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${rfi.humanId}: ${strings.rfis.releaseHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.rfis.releaseLede}</p>
      {rfi.operatorUnattendedAt !== null && (
        <p className="q-note">
          {strings.rfis.witnessUnattended(zonedStamp(rfi.operatorUnattendedAt))}
        </p>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-release-declaration">
          {strings.rfis.releaseDeclarationLabel}
        </label>
        <ServerField field="declaration">
          <textarea
            id="rfi-release-declaration"
            className="q-textarea"
            data-testid="rfi-release-declaration"
            aria-required="true"
            maxLength={2000}
            value={declaration}
            onChange={(event) => {
              setDeclaration(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {!said && (
        <p className="q-note" data-testid="rfi-release-declaration-needed">
          {strings.rfis.releaseDeclarationNeeded}
        </p>
      )}
      <div className="q-field">
        <span className="q-field__label">{strings.rfis.releaseEvidenceLabel}</span>
        <ServerField field="attachment_ids">
          <FilePicker
            files={evidence}
            onChange={setEvidence}
            label={strings.rfis.releaseEvidenceLabel}
            testId="rfi-release-evidence"
            emptyText={strings.rfis.releaseEvidenceEmpty}
          />
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-release-send"
          disabled={busy || !said}
          onClick={() => {
            void send();
          }}
        >
          {strings.rfis.releaseSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

/**
 * Wave RFI ruling 4: the planner names, or changes, the one assignee until conduct
 * starts (POST /api/rfis/{id}/assign). The people offered are the server's eligible
 * assignees; the server still judges (422 rfi.assignee_not_eligible, 409
 * rfi.already_assigned, 409 rfi.assignee_locked), each refusal said inside the dialog.
 */
function RfiAssignDialog({
  rfi,
  onClose,
  onDone,
}: {
  readonly rfi: RfiDetail;
  readonly onClose: () => void;
  readonly onDone: (name: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const [assigneeId, setAssigneeId] = useState("");
  const [reason, setReason] = useState("");
  const [options, setOptions] = useState<readonly PersonOption[]>(
    rfi.assigneeOptions.length > 0 ? rfi.assigneeOptions : rfi.inspectorOptions,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let active = true;
    void listEligibleAssignees(token, rfi.id)
      .then((opts) => {
        if (active && opts.length > 0) {
          setOptions(opts);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [token, rfi.id]);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const saved = await assignRfi(token, rfi.id, {
        assignee_person_id: assigneeId,
        reason: reason.trim() === "" ? null : reason.trim(),
      });
      onDone(
        saved.assigneeDisplayName ??
          options.find((one) => one.personId === assigneeId)?.displayName ??
          "",
      );
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={`${rfi.humanId}: ${strings.rfis.assignHeading}`}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{strings.rfis.assignLede}</p>
      {rfi.assigneeDisplayName !== null && (
        <p className="q-note" data-testid="rfi-assign-current">
          {strings.rfis.factsAssignee}: {rfi.assigneeDisplayName}
        </p>
      )}
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-assign-person">
          {strings.rfis.assignLabel}
        </label>
        <ServerField field="assignee_person_id">
          <select
            id="rfi-assign-person"
            className="q-input"
            data-testid="rfi-assign-person"
            value={assigneeId}
            onChange={(event) => {
              setAssigneeId(event.target.value);
            }}
          >
            <option value="">{strings.rfis.assignChoose}</option>
            {options.map((person) => (
              <option key={person.personId} value={person.personId}>
                {person.displayName}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="rfi-assign-reason">
          {strings.rfis.assignReasonLabel}
        </label>
        <ServerField field="reason">
          <textarea
            id="rfi-assign-reason"
            className="q-textarea"
            data-testid="rfi-assign-reason"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="rfi-assign-send"
          disabled={busy || assigneeId === ""}
          onClick={() => {
            void send();
          }}
        >
          {strings.rfis.assignSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
